import { BizResponse } from '../model/api/common';
import { SealSize, SealStamp, SealStampTableSearchParams, sealStampRenderResponse } from '../model/sealStamp';
import { sendPost, sendPostPageNavigation } from './common';

export async function getSealStampList(searchParams: SealStampTableSearchParams) {
	const { searchType, searchText, searchStatus, contractId, currentPage, rowsPerPage } = searchParams;
	const params = {
		returnType: 'json',
		curr_page: currentPage + 1,
		search_type: searchType,
		search_name: searchText,
		search_status: searchStatus,
		number_per_page: rowsPerPage,
		contract_id: contractId,
		// order_col: orderColumn,
		// order,
	};
	const res = await sendPostPageNavigation<SealStamp[]>('/admin/admin_seal_stamp_process_list_data/', params);
	return res;
}

export async function getDefaultSizeSealStamp(
	signId: number,
	originWidth: number,
	originHeight: number,
	isComponentEnabled: (name: string) => boolean,
): Promise<SealSize> {
	try {
		// 컴포넌트가 활성화되지 않은 경우 아예 API 호출을 하지 않고, 도장의 기본 크기로 찍히게끔 함
		if (isComponentEnabled('CMPNT398_SET_DEFAULT_SIZE_SEAL_STAMP')) {
			const res = await sendPost<BizResponse<{ width?: number; height?: number }>>(
				'/admin/get_default_size_seal_stamp/',
				{ signId },
			);

			const defaultSize = { width: originWidth, height: originHeight };

			const responseData = res.data as { width?: number; height?: number };

			// 1. width와 height가 둘 다 숫자인 경우
			if (typeof responseData.width === 'number' && typeof responseData.height === 'number') {
				return { width: responseData.width, height: responseData.height };
			}

			// 2. width만 존재하고 height는 공란이거나 다른 데이터가 들어온 경우 -> width에 맞춰 height를 비율에 맞게 변경
			if (typeof responseData.width === 'number') {
				const aspectRatio = defaultSize.height / defaultSize.width;
				return { width: responseData.width, height: responseData.width * aspectRatio };
			}

			// 3. height만 존재하고 width는 공란이거나 다른 데이터가 들어온 경우 -> height에 맞춰 width를 비율에 맞게 변경
			if (typeof responseData.height === 'number') {
				const aspectRatio = defaultSize.width / defaultSize.height;
				return { width: responseData.height * aspectRatio, height: responseData.height };
			}

			// 4. 둘 다 공란이거나 데이터의 형태가 맞지 않는 경우 -> 기본 사이즈 반환
			return { width: originWidth, height: originHeight };
		}
		return { width: originWidth, height: originHeight };
	} catch (error) {
		return { width: originWidth, height: originHeight };
	}
}

export async function setSaveDefaultSizeInStampPage(
	signId: string,
	changeWidth: number,
	signHeight: number,
	isComponentEnabled: (name: string) => boolean,
): Promise<SealSize> {
	try {
		if (isComponentEnabled('CMPNT398_SET_DEFAULT_SIZE_SEAL_STAMP')) {
			const res = await sendPost<BizResponse<{ width?: number; height?: number }>>(
				'/admin/save_default_size_in_stamp_page/',
				{ signId, changeWidth, signHeight },
			);

			const responseData = res.data as { width?: number; height?: number };
			const { width: totalWidth, height: totalHeight } = responseData;

			if (typeof totalWidth === 'number' && typeof totalHeight === 'number') {
				return { width: totalWidth, height: totalHeight };
			}
		}
	} catch (error) {
		return { width: changeWidth, height: signHeight };
	}

	// fallback: API 호출 실패하거나 조건 안 맞을 경우
	return { width: changeWidth, height: signHeight };
}

export async function checkIsReStampPossible(
	contractId: string,
	isComponentEnabled: (name: string) => boolean,
): Promise<BizResponse<boolean> | null> {
	/**
	 * 현재 재날인이 가능한 상태인지 확인하는 함수
	 */
	if (isComponentEnabled('CMPNT412_ALTER_SEALING_PROCESS')) {
		const res = await sendPost<boolean>('/contract/check_is_re_stamp_possible/', {
			contract_id: contractId,
		});
		return res;
	}
	return null;
}

export async function checkIsDocumentReStampPossible(
	contractId: string,
	isComponentEnabled: (name: string) => boolean,
	documentId: string | null,
): Promise<BizResponse<boolean> | null> {
	/**
	 * 컴포넌트 419번에서 현재 선택한 document가 재날인이 가능한 상태인지 확인하는 함수
	 */
	if (isComponentEnabled('CMPNT419_MULTI_DOCUMENT_AND_ATTACHMENT_SIGN_BF')) {
		const res = await sendPost<boolean>('/contract/check_is_chosen_document_re_stamp_possible/', {
			contract_id: contractId,
			document_id: documentId,
		});
		return res;
	}
	return null;
}

export async function getSealPageData(
	contractId: string,
	documentId: string | null,
	isComponentEnabled: (name: string) => boolean,
): Promise<BizResponse<sealStampRenderResponse>> {
	/**
	 * 현재 페이지에서 찍었던 인감들의 데이터를 호출
	 */
	if (isComponentEnabled('CMPNT412_ALTER_SEALING_PROCESS')) {
		const params: Record<string, any> = {
			contract_id: contractId,
		};

		if (documentId) {
			params.document_id = documentId;
		}

		const res = await sendPost<sealStampRenderResponse>('/contract/get_seal_stamp_render_data/', params);
		return res;
	}
	return {
		data: {
			stamp_data_list: [],
			stamp_sealing_user_dict: {},
		},
		msg: '',
		rejected: false,
		status: true,
		success: true,
	};
}

export async function resetContractOption(
	contractId: string,
	isComponentEnabled: (name: string) => boolean,
	documentId: string | null,
): Promise<BizResponse<boolean>> {
	if (isComponentEnabled('CMPNT412_ALTER_SEALING_PROCESS')) {
		const res = await sendPost<{ success: boolean }>('/contract/reset_contract_option/', {
			contract_id: contractId,
			document_id: documentId,
		});

		// success 필드만 따서 반환
		return {
			data: res.data.success ?? false,
			msg: res.msg ?? '',
			status: res.status ?? true,
			success: res.success ?? true,
			rejected: res.rejected ?? false,
		};
	}

	// 조건 미충족 시 false 반환
	return {
		data: false,
		msg: '',
		status: true,
		success: true,
		rejected: false,
	};
}
